import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import useDataSourceManager from "hooks/useDataSourceManager";
import { useTheme } from "@mui/material/styles";
import { cloneDeep } from "lodash";

import ModalContainer from "components/ModalContainer";
import GlobalAlert from "components/Notification";
import LinkModal from "components/LinkModal";
import Loading from "../loading";

import { defaultIpAddressObj, MAX_IP_ADDRESS_ENTRIES } from "../util/const";
import { toJSON } from "../util/func";
import Api from "../util/api";
import Form from "../util/form";
import MainView from "./MainView";

const getViewStateTitle = (selectedIpAddress) => {
  return selectedIpAddress ? "Edit IP Group" : "Create New IP Group";
};

const ProfileIpAddressModal = ({
  auth,
  uuidList,
  openModal,
  selectedIpAddress,
  showEditGeneral = true,
  isUnityProSubscribed,
  handleCloseModal,
  onSaveChanges,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { sendRequest, getData, dataMap } = useDataSourceManager();
  const api = Api.init(auth);
  const form = Form.init(theme);

  const [loading, setLoading] = useState(true);
  const [readyToRenderTable, setReadyToRenderTable] = useState(false);
  const [formValue, setFormValue] = useState(null);
  const [alertInfo, setAlertInfo] = useState({
    open: false,
    severity: "",
    message: "",
  });
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const pendingRequestRef = useRef({
    create: false,
    update: false,
  });

  const init = () => {
    setLoading(true);

    if (selectedIpAddress) {
      const { id, name, ipList } = selectedIpAddress.ref;

      setFormValue({
        id,
        name,
        ipAddresses: cloneDeep(ipList),
      });
    } else {
      setFormValue(cloneDeep(defaultIpAddressObj));
    }

    setLoading(false);
    setTimeout(() => setReadyToRenderTable(true), 100);
  };

  const closeModal = () => {
    setFormValue(cloneDeep(defaultIpAddressObj));
    setLoading(true);
    setReadyToRenderTable(false);
    handleCloseModal();
  };

  const getViewTitle = () => {
    return getViewStateTitle(selectedIpAddress);
  };

  function HandleMainView() {
    return {
      changeFormValue: (key, value) => {
        setFormValue((prev) => ({
          ...prev,
          [key]: value,
        }));
      },
      addIpAddresses: (newIpAddresses) => {
        setFormValue((prev) => ({
          ...prev,
          ipAddresses: [...prev.ipAddresses, ...newIpAddresses],
        }));
      },
      importIpAddresses: (replaceFlag, newIpAddresses) => {
        setFormValue((prev) => ({
          ...prev,
          ipAddresses: replaceFlag
            ? newIpAddresses
            : [...prev.ipAddresses, ...newIpAddresses],
        }));
      },
      changeIpAddressItemValue: (index, value) => {
        setFormValue((prev) => {
          const newIpAddresses = [...prev.ipAddresses];
          newIpAddresses[index] = {
            ...newIpAddresses[index],
            ipAddress: value.ipAddress,
            subnet: value.subnet,
          };
          return {
            ...prev,
            ipAddresses: newIpAddresses,
          };
        });
      },
      changeDescriptionItemValue: (index, value) => {
        setFormValue((prev) => {
          const newIpAddresses = [...prev.ipAddresses];
          newIpAddresses[index] = {
            ...newIpAddresses[index],
            description: value,
          };
          return {
            ...prev,
            ipAddresses: newIpAddresses,
          };
        });
      },
      deleteIpAddress: (index) => {
        setFormValue((prev) => {
          const newIpAddresses = prev.ipAddresses.filter((_, itemIndex) => itemIndex !== index);
          return {
            ...prev,
            ipAddresses: newIpAddresses,
          };
        });
      },
      deleteSelectedIpAddresses: (selectedIndexes) => {
        setFormValue((prev) => {
          const newIpAddresses = prev.ipAddresses.filter((_, index) => !selectedIndexes.includes(index));
          return {
            ...prev,
            ipAddresses: newIpAddresses,
          };
        });
      },
      checkFormValid: () => {
        if (!formValue.name || formValue.name.trim() === "") {
          return false;
        }

        if (formValue.ipAddresses.length === 0) {
          return false;
        }

        if (formValue.ipAddresses.length > MAX_IP_ADDRESS_ENTRIES) {
          return false;
        }

        for (let ipItem of formValue.ipAddresses) {
          const ipAddress = ipItem.ipAddress || "";
          if (!form.validateIpAddress(ipAddress)) {
            return false;
          }
          if (!/^\d{1,2}$/.test(ipItem.subnet || "")) {
            return false;
          }
          const subnetNumber = Number(ipItem.subnet);
          if (subnetNumber < 0 || subnetNumber > 32) {
            return false;
          }
        }

        return true;
      },
      handleSave: () => {
        if (!isUnityProSubscribed) {
          setIsLinkModalOpen(true);
          return;
        }

        setLoading(true);

        const finalFormValue = {
          name: formValue.name,
          ipAddresses: formValue.ipAddresses,
        };

        if (selectedIpAddress) {
          const { id } = selectedIpAddress.ref;
          const { apiKey, params } = api.getRequestParams(Api.actionTypeEnum.update, id);
          const payload = toJSON(finalFormValue, id);

          pendingRequestRef.current.update = true;
          sendRequest(apiKey, uuidList.update, params, payload);
        } else {
          const payload = toJSON(finalFormValue);
          const { apiKey, params } = api.getRequestParams(Api.actionTypeEnum.create);

          pendingRequestRef.current.create = true;
          sendRequest(apiKey, uuidList.create, params, payload);
        }
      },
    };
  }

  useEffect(() => {
    if (openModal) {
      init();
    } else {
      setFormValue(cloneDeep(defaultIpAddressObj));
      setLoading(true);
      setReadyToRenderTable(false);
    }
  }, [openModal]);

  useEffect(() => {
    const createResponse = getData(uuidList.create);

    if (createResponse && pendingRequestRef.current.create) {
      pendingRequestRef.current.create = false;

      if (createResponse.hasOwnProperty("code") && createResponse?.code !== 200) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: createResponse?.message,
        });
        setLoading(false);
      } else {
        onSaveChanges();
      }
    }
  }, [dataMap?.[uuidList.create]]);

  useEffect(() => {
    const updateResponse = getData(uuidList.update);

    if (updateResponse && pendingRequestRef.current.update) {
      pendingRequestRef.current.update = false;

      if (updateResponse.hasOwnProperty("code") && updateResponse?.code !== 200) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: updateResponse?.message,
        });
        setLoading(false);
      } else {
        onSaveChanges();
      }
    }
  }, [dataMap?.[uuidList.update]]);

  if (!openModal) {
    return null;
  }

  return (
    <>
      <ModalContainer display={openModal} modalWidth="auto">
        {loading && <Loading modalModel />}
        {formValue && (
          <MainView
            getViewTitle={getViewTitle}
            form={form}
            formValue={formValue}
            readyToRenderTable={readyToRenderTable}
            closeModal={closeModal}
            handleMainView={HandleMainView()}
            isEditMode={!!selectedIpAddress}
            showEditGeneral={showEditGeneral}
            auth={auth}
          />
        )}
      </ModalContainer>

      <GlobalAlert
        open={alertInfo.open}
        severity={alertInfo.severity}
        message={alertInfo.message}
        onClose={() => setAlertInfo((prev) => ({ ...prev, open: false }))}
      />

      <LinkModal
        open={isLinkModalOpen}
        onClose={() => setIsLinkModalOpen(false)}
        content="IP Address Profile is a Nuclias Unity Pro feature. Please subscribe to Nuclias Unity Pro to save these changes."
        transparentBackdrop
        onLinkClick={() => {
          setIsLinkModalOpen(false);
          const isMspScope = auth?.realm?.hasManageOrg === true && !auth?.orgs?.id;
          navigate(isMspScope ? "/msp/license" : "/license");
        }}
      />
    </>
  );
};

export default ProfileIpAddressModal;
